/**
 * 画像処理パイプライン用の型定義と定数。
 * カーセンサー掲載仕様 (docs/SPEC.md §2 準拠)。
 */

// ===== カーセンサー仕様定数 =============================================

export const CARSENSOR_SPEC = {
  /** 目標アスペクト比 (4:3) */
  ASPECT_RATIO: 4 / 3,
  /** アスペクト比許容誤差 (端数処理で ±0.001 程度発生) */
  ASPECT_TOLERANCE: 0.01,
  /** 出力の最大幅 (px) */
  MAX_WIDTH: 2560,
  /** 出力の最大高さ (px) */
  MAX_HEIGHT: 1920,
  /** 入力の最小幅 (px) */
  MIN_WIDTH: 640,
  /** 入力の最小高さ (px) */
  MIN_HEIGHT: 480,
  /** 最大ファイルサイズ (10.4 MB、安全を見て 10 MB を上限扱い) */
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
  /** JPEG 品質 (SPEC §2.4) */
  JPEG_QUALITY: 0.95,
  /** 1 車両あたりの最大枚数 */
  MAX_PHOTOS_PER_VEHICLE: 80,
} as const;

// ===== 処理オプション ==================================================

export interface ProcessOptions {
  /** JPEG エンコード品質 (0.0 〜 1.0、デフォルト 0.95) */
  readonly quality?: number;
  /** 明度・コントラスト自動補正を適用するか (デフォルト true) */
  readonly autoEnhance?: boolean;
  /** 強制的に PNG 出力するか (ヒーロー写真のみ true、デフォルト false) */
  readonly outputPng?: boolean;
  /**
   * Wave 19: 車体の bounding box (0-1 正規化)。指定時は smart crop (車中心
   * 4:3 ズームクロップ) が実行される。未指定なら従来の中央クロップ。
   */
  readonly smartCropBbox?: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  /** Wave 19: 出力フレーム内で車が占めるべき比率 (デフォルト 0.82) */
  readonly smartCropFill?: number;
}

// ===== 処理結果 =========================================================

export interface ProcessedImage {
  /** エンコード済みバイナリ */
  readonly blob: Blob;
  /** 出力幅 (px) */
  readonly width: number;
  /** 出力高さ (px) */
  readonly height: number;
  /** MIME タイプ */
  readonly mimeType: 'image/jpeg' | 'image/png';
  /** 処理所要時間 (ms) */
  readonly processingTimeMs: number;
}

// ===== バリデーションエラー =============================================

export type ValidationErrorCode =
  | 'TOO_SMALL'
  | 'UNSUPPORTED_FORMAT'
  | 'TOO_MANY_FILES'
  | 'EMPTY_FILE';

export interface ValidationError {
  readonly code: ValidationErrorCode;
  readonly message: string;
  readonly file?: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

// ===== Result 型 (CODE_STANDARDS.md §エラーハンドリング) =================

export type Result<T, E = ValidationError> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

// ===== クロップ矩形 =====================================================

export interface CropRect {
  /** 元画像の x 起点 */
  readonly sx: number;
  /** 元画像の y 起点 */
  readonly sy: number;
  /** クロップ幅 */
  readonly sw: number;
  /** クロップ高さ */
  readonly sh: number;
}

// ===== リサイズ計算結果 =================================================

export interface ResizePlan {
  /** 出力幅 (縮小後) */
  readonly width: number;
  /** 出力高さ (縮小後) */
  readonly height: number;
  /** 縮小比率 (1.0 以下。1.0 は拡大禁止のため現状維持) */
  readonly scale: number;
  /** リサイズ不要 (既に仕様内) なら true */
  readonly noop: boolean;
}
